/** Set on a text element while its text doesn't fit, so its end fades. */
export const TEXT_TRUNCATED_ATTRIBUTE = 'data-truncated'

function isOverflowing(element: HTMLElement) {
  return element.scrollWidth > element.clientWidth
}

/** Marks a text element while its text overflows. Text that fits keeps a crisp end. */
export function markTextTruncation(element: HTMLElement) {
  const truncated = isOverflowing(element)
  if (element.hasAttribute(TEXT_TRUNCATED_ATTRIBUTE) !== truncated) {
    element.toggleAttribute(TEXT_TRUNCATED_ATTRIBUTE, truncated)
  }
}

// One observer serves every marked element. It only marks the DOM (no React
// state), and reads every element before writing any, so a resize lays the
// page out once per frame. Its callback runs before the browser paints, so a
// box that narrows (a row's icons showing on hover) fades in the same frame.
let observer: ResizeObserver | null = null

/** Keeps an element marked while its text overflows; returns the cleanup. */
export function observeTextTruncation(element: HTMLElement): () => void {
  markTextTruncation(element)
  if (typeof ResizeObserver === 'undefined') return () => {}
  observer ??= new ResizeObserver((entries) => {
    const elements = entries.map((entry) => entry.target as HTMLElement)
    const truncated = elements.map(isOverflowing)
    elements.forEach((target, index) => {
      if (target.hasAttribute(TEXT_TRUNCATED_ATTRIBUTE) !== truncated[index]) {
        target.toggleAttribute(TEXT_TRUNCATED_ATTRIBUTE, truncated[index])
      }
    })
  })
  observer.observe(element)
  return () => observer?.unobserve(element)
}
